import React, { useEffect } from 'react';
import CustomRechnung from './customRechnung';

interface priceInfo {
  gross: number;
  net: number;
  rangeLabel: string;
  vat: number;
  vatRate: number;
}

interface BookingInvoiceInfo {
  costCenterName: string;
  companyName: string;
  invoiceNumber: string;
  invoiceCreatedDate: string;
  bookingStatus: string;
  bookingID: string;
  licensePlate: string;
  tripDate: string;
  startKm: number;
  endKm: number;
  totalKm: number;
  internalNotes: string;
  price: priceInfo;
  isCancellation: boolean;
}

interface InvoiceItem {
  pos: number;
  artikel: string;
  anzahl: number;
  preis: number;
  summe: number;
}

interface BookingRechnungProps {
  bookings: BookingInvoiceInfo[];
  customInvoiceItems: InvoiceItem[];
  onCustomInvoiceItemsChange: (items: InvoiceItem[]) => void;
  customInvoiceAddress: string;
  onCustomInvoiceAddressChange: (address: string) => void;
  customInvoiceIsCancellation: boolean;
  onCustomInvoiceIsCancellationChange: (isCancel: boolean) => void;
  customInvoiceOldNumber: string;
  onCustomInvoiceOldNumberChange: (oldNumber: string) => void;
  customInvoiceOldDate: string;
  onCustomInvoiceOldDateChange: (oldDate: string) => void;
}

function convertBookingsToCustomInvoiceItems(
  bookings: BookingInvoiceInfo[],
  isCancellation: boolean = false
): InvoiceItem[] {
  return bookings.map((booking, index) => {
    const hint = booking.internalNotes ? `Hinweis: ${booking.internalNotes}` : '';

    const description = `
${isCancellation ? 'Storno für ' : ''}Fahrzeugüberführung bis ${booking.price?.rangeLabel}
Tour-Nummer: ${booking.bookingID}
Datum der Überführung: ${booking.tripDate}
Kennzeichen/FIN: ${booking.licensePlate}
Start-Kilometerstand: ${new Intl.NumberFormat('de-DE').format(booking.startKm)}
End-Kilometerstand: ${new Intl.NumberFormat('de-DE').format(booking.endKm)}
Gefahrene Strecke: ${new Intl.NumberFormat('de-DE').format(booking.totalKm)} km
${hint}
`.trim();

    return {
      pos: index + 1,
      artikel: description,
      anzahl: isCancellation ? -1 : 1,
      preis: booking.price?.net,
      summe: isCancellation ? -booking.price?.net : booking.price?.net,
      isBookingItem: true, // <-- flag it
    };
  });
}

function BookingRechnung({
  bookings,
  customInvoiceItems,
  onCustomInvoiceItemsChange,
  customInvoiceAddress,
  onCustomInvoiceAddressChange,
  customInvoiceIsCancellation,
  onCustomInvoiceIsCancellationChange,
  customInvoiceOldNumber,
  onCustomInvoiceOldNumberChange,
  customInvoiceOldDate,
  onCustomInvoiceOldDateChange,
}: BookingRechnungProps) {
  useEffect(() => {
    if (bookings.length > 0) {
      const isCancellation = bookings[0]?.isCancellation ?? false;
      const converted = convertBookingsToCustomInvoiceItems(bookings, isCancellation);
      onCustomInvoiceItemsChange(converted);
      onCustomInvoiceIsCancellationChange(isCancellation);
      onCustomInvoiceOldNumberChange(bookings[0]?.invoiceNumber ? 'KEP-'+bookings[0]?.invoiceNumber : '');
      onCustomInvoiceOldDateChange(bookings[0]?.invoiceCreatedDate ?? '');

      const companyName = bookings[0]?.companyName ?? '';
      const costCenterName = bookings[0]?.costCenterName ?? '';

      let address = `Autohaus Adelbert Moll GmbH & Co. KG\nKostenstelle: ${costCenterName}\n`;

      if (companyName.includes('IKA')) {
        address += `Ikarusstraße 20\n40474 Düsseldorf`;
      } else if (companyName.includes('KRE')) {
        address += `Krefelder Straße 117\n40549 Düsseldorf`;
      }

      onCustomInvoiceAddressChange(address);
    }
  }, [bookings]);

  return (
    <CustomRechnung
      customInvoiceItems={customInvoiceItems}
      onCustomInvoiceItemsChange={onCustomInvoiceItemsChange}
      customInvoiceAddress={customInvoiceAddress}
      onCustomInvoiceAddressChange={onCustomInvoiceAddressChange}
      customInvoiceIsCancellation={customInvoiceIsCancellation}
      onCustomInvoiceIsCancellationChange={onCustomInvoiceIsCancellationChange}
      customInvoiceOldNumber={customInvoiceOldNumber}
      onCustomInvoiceOldNumberChange={onCustomInvoiceOldNumberChange}
      customInvoiceOldDate={customInvoiceOldDate}
      onCustomInvoiceOldDateChange={onCustomInvoiceOldDateChange}
    />
  );
}

export default BookingRechnung;